// Content blame: the file X-ray's per-line provenance, generalized to
// conversations. A conversation's blocks came from somewhere - a spawner's
// assignment, an earlier request of the same thread, a child's final report,
// a compacted window's summary - and because harnesses COPY content (context
// editing retains turns verbatim, merge results embed the child's report,
// compaction pastes its summary into the next window's opening), literal
// span matching recovers those edges without any stored lineage key.
//
// Three edge types, all string evidence and never fuzzy:
//
//   trimmed-from   a context-edited re-send retains a subset of an earlier
//                  request's turns verbatim. If most of a chain's opening
//                  turns appear, in order, among another conversation's
//                  turns, the chain is that conversation re-sent after a
//                  trim - not a new agent, and attributable to its thread.
//
//   merged-report-from
//                  an async spawn's tool_result is a launch ack; the child's
//                  actual report arrives later and is embedded verbatim when
//                  it merges. A result block whose text matches a child's
//                  final assistant message is that child's merge point.
//
//   compacted-from
//                  a compaction summary is a rewrite of the source turns -
//                  unmatchable as a whole - but it QUOTES them verbatim, and
//                  the summary itself is pasted into the next window's first
//                  user message. A banner-opening chain whose quoted
//                  fragments appear in a conversation was compacted from it.
//
// Turn-level matchers work on FINGERPRINTS (normalized-text hashes per
// turn), so callers can ship compact key lists across module boundaries
// instead of whole conversations.

// Whitespace-insensitive normalization: context editing and re-serialization
// may reflow text, but they do not reword it.
const normalize = (s) => String(s || "").replace(/\s+/g, " ").trim();

// FNV-1a over the normalized text: fast, deterministic, no crypto needed -
// a collision costs one wrong hint chip, not data integrity.
function fnv1a(s) {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(36);
}

function blockText(b) {
  if (!b || typeof b !== "object") return "";
  switch (b.type) {
    case "text":
      return String(b.text || "");
    case "thinking":
      return String(b.thinking || "");
    case "tool_use": {
      let input = "";
      if (typeof b.input === "string") input = b.input;
      else if (b.input != null) {
        try {
          input = JSON.stringify(b.input);
        } catch {
          input = "";
        }
      }
      return `${b.name || ""} ${input}`;
    }
    // tool_result content is EXCLUDED on purpose: context editing clears or
    // truncates results inside retained turns, so hashing them would break
    // the verbatim-retention premise trimmed-from matching rests on. The
    // stable spans are what the model authored (text, thinking, tool calls).
    default:
      return "";
  }
}

/**
 * One fingerprint per non-system turn: role plus the hash of its normalized
 * STABLE block text (text / thinking / tool_use - see blockText for why
 * tool_result is excluded). System turns are excluded too - every
 * conversation of a session shares the same system prompt, which would make
 * everything "match". A turn left with no stable text (a bare tool_result
 * turn) contributes no fingerprint.
 */
export function turnFingerprints(turns, cap = 40) {
  const out = [];
  for (const t of Array.isArray(turns) ? turns : []) {
    if (!t || t.role === "system") continue;
    const text = normalize((t.blocks || []).map(blockText).join(" "));
    if (!text) continue;
    out.push(`${t.role}:${fnv1a(text)}`);
    if (out.length >= cap) break;
  }
  return out;
}

// A trim match must cover at least this share of the candidate's opening
// fingerprints, and at least this many turns, before it is claimed. A
// context-edited re-send retains most of its head; two unrelated
// conversations share almost none of it.
const TRIM_MIN_COVERAGE = 0.6;
const TRIM_MIN_MATCHED = 3;

/**
 * Does `candidateKeys` (a chain's opening turn fingerprints) read as a
 * context-trimmed re-send of the conversation with `sourceKeys`?
 *
 * Matches as an ordered subsequence - editing drops turns but never reorders
 * them. Returns { matched, coverage, matchedTurns } with matched=false when
 * the evidence is below the floors.
 */
export function matchesTrimmedFrom(candidateKeys, sourceKeys) {
  const cand = Array.isArray(candidateKeys) ? candidateKeys : [];
  const src = Array.isArray(sourceKeys) ? sourceKeys : [];
  if (!cand.length || !src.length) return { matched: false, coverage: 0, matchedTurns: 0 };
  let si = 0;
  let matchedTurns = 0;
  for (const k of cand) {
    while (si < src.length && src[si] !== k) si++;
    if (si < src.length) {
      matchedTurns++;
      si++;
    }
  }
  const coverage = matchedTurns / cand.length;
  return {
    matched: matchedTurns >= TRIM_MIN_MATCHED && coverage >= TRIM_MIN_COVERAGE,
    coverage,
    matchedTurns,
  };
}

// Reports run long; a 240-char shared prefix distinguishes any two real
// reports while surviving trailing truncation on either side.
const REPORT_MATCH_PREFIX = 240;
const REPORT_MIN_OVERLAP = 40;

/**
 * Does `resultText` (a spawn's merge/result block) carry `reportText` (a
 * child conversation's final assistant message)? Prefix equality after
 * normalization, plus containment for results that wrap the report in
 * harness framing.
 */
export function matchesMergedReport(resultText, reportText) {
  const result = normalize(resultText);
  const report = normalize(reportText);
  if (!result || !report) return false;
  const n = Math.min(REPORT_MATCH_PREFIX, result.length, report.length);
  if (n >= REPORT_MIN_OVERLAP && result.slice(0, n) === report.slice(0, n)) return true;
  const probe = report.slice(0, Math.min(REPORT_MATCH_PREFIX, report.length));
  return probe.length >= REPORT_MIN_OVERLAP && result.includes(probe);
}

// A post-compaction context window opens with a continuation banner and the
// summarizer's output pasted verbatim into the first user message. The
// summary is an LLM rewrite - unmatchable against the source turns as a
// whole - but it QUOTES source material verbatim (user messages, paths,
// exact strings), and those quoted fragments are literal evidence of which
// conversation was compacted.
const COMPACTION_BANNER = /session is being continued from a previous conversation/i;
// Harness reminders are shared boilerplate injected into first user
// messages; a "quote" lifted from one would match every conversation.
const REMINDER_SPAN = /<system-reminder>[\s\S]*?(?:<\/system-reminder>|$)/g;
const FRAGMENT_MIN = 40;
const FRAGMENT_MAX = 400;
const FRAGMENT_CAP = 24;
const COMPACTED_MIN_FRAGMENTS = 2;

/**
 * Detect a compaction-restart opening and extract its verbatim anchors: the
 * double-quoted spans of the embedded summary, minimum FRAGMENT_MIN chars so
 * a generic phrase cannot anchor a claim. Returns { fragments } or null when
 * the first user message carries no continuation banner.
 */
export function compactionOpening(turns) {
  const list = Array.isArray(turns) ? turns : [];
  const first = list.find((t) => t?.role !== "system");
  if (!first || first.role !== "user") return null;
  let text = "";
  for (const b of first.blocks || []) {
    if (b?.type === "tool_result") break;
    if (b?.type === "text" && typeof b.text === "string") text += `\n${b.text}`;
  }
  text = text.replace(REMINDER_SPAN, " ");
  if (!COMPACTION_BANNER.test(text)) return null;
  const fragments = [];
  const seen = new Set();
  const quoted = /"([^"\n]+)"/g;
  let m;
  while ((m = quoted.exec(text)) && fragments.length < FRAGMENT_CAP) {
    const frag = normalize(m[1]).slice(0, FRAGMENT_MAX);
    if (frag.length < FRAGMENT_MIN || seen.has(frag)) continue;
    seen.add(frag);
    fragments.push(frag);
  }
  return { fragments };
}

/**
 * Does a compaction summary's quoted material appear verbatim in
 * `sourceText` (a conversation's stable text)? Two independent fragments
 * are required - one shared quote can be coincidence, two the same
 * conversation. Short fragments are ignored regardless of caller.
 */
export function matchesCompactedFrom(fragments, sourceText) {
  const src = normalize(sourceText);
  let matchedFragments = 0;
  for (const f of Array.isArray(fragments) ? fragments : []) {
    const frag = normalize(f);
    if (frag.length < FRAGMENT_MIN) continue;
    if (src && src.includes(frag)) matchedFragments++;
  }
  return { matched: matchedFragments >= COMPACTED_MIN_FRAGMENTS, matchedFragments };
}

/**
 * A conversation's stable text as one normalized string - the containment
 * target for compacted-from anchors. Same block policy as turnFingerprints
 * (system turns and tool_result content excluded).
 */
export function conversationStableText(turns) {
  const out = [];
  for (const t of Array.isArray(turns) ? turns : []) {
    if (!t || t.role === "system") continue;
    const text = (t.blocks || []).map(blockText).join(" ");
    if (text.trim()) out.push(text);
  }
  return normalize(out.join(" "));
}

/**
 * The final assistant text of a parsed conversation - the report a merge
 * would embed. Empty string when the conversation ends without one.
 */
export function finalAssistantText(turns) {
  const list = Array.isArray(turns) ? turns : [];
  for (let i = list.length - 1; i >= 0; i--) {
    const t = list[i];
    if (t?.role !== "assistant") continue;
    for (const b of t.blocks || []) {
      if (b?.type === "text" && typeof b.text === "string" && b.text.trim()) return b.text;
    }
    return "";
  }
  return "";
}
